import React, { useContext, useEffect, useState } from "react";
import QuickStatsBox from "../components/QuickStatsBox";
import SingleUserTestimonial from "../components/SingleUserTestimonial";
import SuccessAlert from "../components/SuccessAlert";
import ErrorAlert from "../components/ErrorAlert";
import NothingToShow from "../../components/NothingToShow";
import { Chart, defaults } from "chart.js/auto";
import { Doughnut, Line } from "react-chartjs-2";
import { ExpiringContext } from "../../context/ExpiringBookings";

export default function Dashboard() {
  // Using Context
  const expireCheckComplete = useContext(ExpiringContext);

  // Setting Charts Global Options
  defaults.maintainAspectRatio = false;
  defaults.responsive = true;

  // State Handling
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fetchedData, setFetchedData] = useState();
  const [fetchedBookings, setFetchedBookings] = useState([]);
  const [itemCount, setItemCount] = useState();
  const [monthlyBookings, setMonthlyBookings] = useState();

  // --------- Reading/Fetching Testimonials/Feedbacks From Database ---------
  const readingData = async () => {
    const response = await fetch("https://hotelbnb-backend.onrender.com/read-contacts");
    const result = await response.json();

    if (!response.ok) {
      setSuccess("");
      setError("Feedbacks can't be fetched");
    } else if (response.ok) {
      setTimeout(() => {
        setError("");
      }, 10000);
      const filteringFeedbacks = Array.from(
        new Set(
          result.filter((feedback) => {
            return feedback.user_query_type == "Feedback";
          })
        )
      );
      setFetchedData(filteringFeedbacks);
    }
  };

  // Bookings, Users and Rooms Count
  const countBookingsUsersRooms = async () => {
    const response = await fetch("https://hotelbnb-backend.onrender.com/dashboard-count");

    if (response.ok) {
      const result = await response.json();
      setItemCount(result);
    }
  };

  // --------- Reading/Fetching Bookings Of This Year From Database ---------
  const readingBookings = async () => {
    const response = await fetch("https://hotelbnb-backend.onrender.com/read-year-bookings");
    const result = await response.json();

    if (!response.ok) {
      setError("Bookings can't be fetched");
    } else if (response.ok) {
      setTimeout(() => {
        setError("");
      }, 10000);
      setFetchedBookings(result);
    }
  };

  const arrangeBookings = () => {
    if (fetchedBookings.length > 0) {
      const gettingMonthlyBookings = fetchedBookings.reduce(
        (accBooking, curBooking) => {
          const individualMonth = new Date(curBooking.booking_from).getMonth();
          accBooking[individualMonth] = (accBooking[individualMonth] || 0) + 1;
          return accBooking;
        },
        Array(12).fill(0)
      );
      setMonthlyBookings(gettingMonthlyBookings);
    }
  };

  useEffect(() => {
    readingData();
    readingBookings();
  }, []);

  useEffect(() => {
    countBookingsUsersRooms();
  }, [expireCheckComplete]);

  useEffect(() => {
    arrangeBookings();
  }, [fetchedBookings]);

  // --------- Deleting ---------
  const deleteHandler = async (contactId) => {
    const response = await fetch(
      `https://hotelbnb-backend.onrender.com/delete-contact/${contactId}`,
      {
        method: "DELETE",
      }
    );

    const result = await response.json();

    if (!response.ok) {
      setSuccess("");
      setError("Feedback can't be deleted");
    } else if (response.ok) {
      setError("");
      setSuccess("Feedback Deleted Successfully");

      setTimeout(() => {
        setSuccess("");
      }, 2000);
    }

    // Reading the feedbacks again
    readingData();
  };

  return (
    <>
      {/* Showing Alert based on http request response Start */}
      {success && <SuccessAlert successMsg={success} />}
      {error && <ErrorAlert errorMsg={error} />}
      {/* Showing Alert based on http request response End */}

      {/* Quick Stats Boxes Start */}
      <div className="col-12 d-flex flex-wrap gap-4 p-0 quick-stats-boxes">
        <QuickStatsBox
          totalNumber={itemCount?.bookings}
          boxTitle="All Bookings"
          icon="/images/icons/allBookings-count.png"
        />
        <QuickStatsBox
          totalNumber={itemCount?.users}
          boxTitle="All Users"
          icon="/images/icons/allUsersCount.png"
        />
        <QuickStatsBox
          totalNumber={itemCount?.rooms}
          boxTitle="All Rooms"
          icon="/images/icons/allRooms-count.png"
          w="33px"
        />
      </div>
      {/* Quick Stats Boxes End */}

      {/* Charts Section (All Users Doughnut Chart and Bookings Line Chart) Start */}
      <div className="col-12 d-flex flex-wrap gap-4 justify-content-between align-items-stretch usersAndBookings-stats-chart">
        {/* Doughnut Chart Start */}
        <div className="col-xl-4 col-12 users-stats-chart d-flex justify-content-center align-items-center">
          <Doughnut
            data={{
              labels: ["Bookings", "Users"],
              datasets: [
                {
                  data: [itemCount?.activeBookings, itemCount?.users],
                  backgroundColor: ["#ffb33b", "#0f2491"],
                  hoverBorderColor: "#f5f5f589",
                },
              ],
            }}
            options={{
              cutout: 40,
              animation: {
                animateRotate: true,
                duration: 2000,
              },
              plugins: {
                title: {
                  text: "Active Bookings & Users",
                  display: true,
                  align: "center",
                  font: {
                    size: 18,
                    weight: "bold",
                  },
                  color: "#1B212F",
                  position: "bottom",
                  padding: {
                    top: 20,
                  },
                },
                legend: {
                  position: `${window.innerWidth <= 1200 ? "bottom" : "right"}`,
                },
              },
            }}
          />
        </div>
        {/* Doughnut Chart End */}

        {/* Line Chart Start */}
        <div className="col-xl col-12 bookings-stats-chart d-flex justify-content-center align-items-center">
          {monthlyBookings && (
            <Line
              data={{
                labels: [
                  "Jan",
                  "Feb",
                  "Mar",
                  "Apr",
                  "May",
                  "Jun",
                  "Jul",
                  "Aug",
                  "Sep",
                  "Oct",
                  "Nov",
                  "Dec",
                ],
                datasets: [
                  {
                    label: "All Bookings",
                    data: [...monthlyBookings],
                    borderColor: "#0f2491",
                    backgroundColor: "#ffb33b",
                    tension: 0.1,
                  },
                ],
              }}
              options={{
                animations: {
                  tension: {
                    duration: 1000,
                    easing: "linear",
                    from: 0.1,
                    to: 0.3,
                    loop: true,
                  },
                },
                plugins: {
                  title: {
                    text: "Monthly Bookings",
                    display: true,
                    align: "center",
                    font: {
                      size: 18,
                      weight: "bold",
                    },
                    color: "#1B212F",
                    position: "bottom",
                    padding: {
                      top: 10,
                    },
                  },
                  legend: {
                    position: "bottom",
                  },
                },
              }}
            />
          )}
        </div>
        {/* Line Chart End */}
      </div>
      {/* Charts Section (All Users Doughnut Chart and Bookings Line Chart) End */}

      {/* User Testimonials Start */}
      <div className="col-12 p-0 admin-user-testimonials">
        {/* Heading */}
        <h4 className="mb-4">Latest Customer Review</h4>
        {/* All Testimonials */}
        <div className="col-12 p-0 d-flex flex-wrap justify-content-between">
          {!fetchedData?.length ? <NothingToShow /> : ""}

          {fetchedData?.slice(0, 4).map((singleFeedback) => {
            return (
              <SingleUserTestimonial
                key={singleFeedback._id}
                userImg={singleFeedback.user_img}
                userName={singleFeedback.user_name}
                userEmail={singleFeedback.user_email}
                testimonialText={singleFeedback.user_message}
                contactId={singleFeedback._id}
                deleteHandler={deleteHandler}
              />
            );
          })}
        </div>
      </div>
      {/* User Testimonials End */}
    </>
  );
}
